import type { AlertAck } from "@khazzin/api-client";
import type { OutboxEntry } from "@khazzin/api-client/sync";

/**
 * The driver's alerts that wait for an acknowledgment (EP12-S15, DA-12-13):
 * what the server holds open, less what the phone has already acknowledged
 * and not yet sent. The acknowledgment is a queued command like any other,
 * so it works with no signal; the phone's key is its `clientEventId`.
 */

/** The outbox command that acknowledges an alert. */
export const ACK = "ack_alert";

/** The open alerts the driver has not acknowledged on this phone, oldest first. */
export function awaiting(alerts: readonly AlertAck[], outbox: readonly OutboxEntry[]): AlertAck[] {
	const queued = new Set(
		outbox.filter((e) => e.command === ACK).map((e) => String(e.payload.notificationId)),
	);
	return alerts.filter(
		(a) => (a.status === "pending" || a.status === "escalated") && !queued.has(a.id),
	);
}

/** Whether an acknowledgment is waiting in the outbox for a signal. */
export function ackQueued(outbox: readonly OutboxEntry[]): boolean {
	return outbox.some((e) => e.command === ACK && e.state === "queued");
}

/** The command's payload: the alert, and when the driver tapped. */
export function ackPayload(notificationId: string, at: Date): Record<string, unknown> {
	return { notificationId, ackedAt: at.toISOString() };
}
